// src/components/Indicadores/BarEquilibrio/BarEquilibrio.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import BarChart from '../../../Charts/BarChart';
import { obtenerPuntoDeEquilibrio } from '../../../../store/actions/puntoDeEquilibrio';

const useStyles = makeStyles(() => ({
  chartWrapper: { position: 'relative', width: '100%', height: '100%' },
  toolbarFix: { '& .apexcharts-toolbar': { right: '12px !important', top: '6px !important' } },
  spinnerOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.6)',
    zIndex: 2
  }
}));

const INDICADOR_ID = 76;

const MESES = [
  { nombre: 'Enero', cod: '01' },
  { nombre: 'Febrero', cod: '02' },
  { nombre: 'Marzo', cod: '03' },
  { nombre: 'Abril', cod: '04' },
  { nombre: 'Mayo', cod: '05' },
  { nombre: 'Junio', cod: '06' },
  { nombre: 'Julio', cod: '07' },
  { nombre: 'Agosto', cod: '08' },
  { nombre: 'Septiembre', cod: '09' },
  { nombre: 'Octubre', cod: '10' },
  { nombre: 'Noviembre', cod: '11' },
  { nombre: 'Diciembre', cod: '12' }
];

const SIMBOLO_A_ID = { $: 'PES', US$: 'DOL', '€': 'EUR' };

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const getIdMoneda = ({ idMoneda, monedaSimbolo }) =>
  idMoneda || (monedaSimbolo && SIMBOLO_A_ID[monedaSimbolo]) || 'PES';

const BarEquilibrio = ({
  empresaId,
  anio, // año seleccionado (ej: 2025)
  idMoneda,
  monedaSimbolo,
  title,
  height = 380,
  colors,
  noBoxShadow
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const anioNum = useMemo(() => (Number.isFinite(anio) ? anio : new Date().getFullYear()), [anio]);
  const monedaId = useMemo(() => getIdMoneda({ idMoneda, monedaSimbolo }), [idMoneda, monedaSimbolo]);

  // Mes actual del sistema (1..12) para fijar la ventana móvil
  const mesActual = useMemo(() => new Date().getMonth() + 1, []);
  const prevYear = anioNum - 1;

  // Categorías: armamos 13 (prevYear mesActual..12 + 1..mesActual anioNum) y nos quedamos con 12 (sacamos la más vieja)
  const categories = useMemo(() => {
    const all13 = [];
    for (let m = mesActual; m <= 12; m++) all13.push(`${MESES[m - 1].nombre}/${prevYear}`);
    for (let m = 1; m <= mesActual; m++) all13.push(`${MESES[m - 1].nombre}/${anioNum}`);
    // si vinieron 13, drop del más viejo (primer elemento)
    return all13.length > 12 ? all13.slice(1) : all13;
  }, [mesActual, prevYear, anioNum]);

  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([
    { name: 'Ventas', data: [] },
    { name: 'Punto de equilibrio', data: [] }
  ]);
  const [maxEq, setMaxEq] = useState(0);

  useEffect(() => {
    let cancel = false;

    const fetchBothYears = async () => {
      if (!empresaId || !anioNum || !monedaId) {
        setSeries([
          { name: 'Monto', data: [] },
          { name: 'Punto de equilibrio', data: [] }
        ]);
        setMaxEq(0);
        return;
      }

      setLoading(true);
      try {
        const [dataPrev, dataCurr] = await Promise.all([
          dispatch(
            obtenerPuntoDeEquilibrio({
              mes: 12,
              anio: prevYear,
              idMoneda: monedaId,
              idEmpresa: Number(empresaId),
              indicadorId: INDICADOR_ID
            })
          ),
          dispatch(
            obtenerPuntoDeEquilibrio({
              mes: 12,
              anio: anioNum,
              idMoneda: monedaId,
              idEmpresa: Number(empresaId),
              indicadorId: INDICADOR_ID
            })
          )
        ]);
        if (cancel) return;

        const idxYear = (arr) => {
          const m = {};
          if (Array.isArray(arr)) {
            arr.forEach((it) => {
              const mesCod = String(it.mes ?? it.Mes ?? '').padStart(2, '0');
              m[mesCod] = {
                monto: toNum(it.monto ?? it.importe ?? it.total),
                eq: toNum(it.montoDeEquilibrio ?? it.montoEquilibrio)
              };
            });
          }
          return m;
        };

        const mapPrev = idxYear(dataPrev);
        const mapCurr = idxYear(dataCurr);

        const montoVentana = [];
        const eqVentana = [];
        let maxEqLocal = 0;

        for (let m = mesActual; m <= 12; m++) {
          const cod = String(m).padStart(2, '0');
          const reg = mapPrev[cod] || {};
          montoVentana.push(reg.monto ?? null);
          eqVentana.push(reg.eq ?? null);
          if (reg.eq > maxEqLocal) maxEqLocal = reg.eq;
        }
        for (let m = 1; m <= mesActual; m++) {
          const cod = String(m).padStart(2, '0');
          const reg = mapCurr[cod] || {};
          montoVentana.push(reg.monto ?? null);
          eqVentana.push(reg.eq ?? null);
          if (reg.eq > maxEqLocal) maxEqLocal = reg.eq;
        }

        // si son 13, sacamos el más viejo (posición 0) para que queden 12 y calcen con categories
        if (montoVentana.length > 12) {
          montoVentana.shift();
          eqVentana.shift();
        }

        setSeries([
          { name: 'Monto', data: montoVentana },
          { name: 'Punto de equilibrio', data: eqVentana }
        ]);
        setMaxEq(maxEqLocal || 0);
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    fetchBothYears();
    return () => {
      cancel = true;
    };
  }, [dispatch, empresaId, anioNum, monedaId, mesActual, prevYear]);

  const anyData =
    series?.length > 0 && (series[0]?.data?.some((v) => v != null) || series[1]?.data?.some((v) => v != null));

  const y2Max = useMemo(() => {
    if (!maxEq || maxEq <= 0) return 10;
    const padded = Math.ceil(maxEq * 1.2);
    return padded < 10 ? 10 : padded;
  }, [maxEq]);

  return (
    <div className={`${classes.chartWrapper} ${classes.toolbarFix}`}>
      <BarChart
        title={title ?? `Ventas vs Punto de equilibrio — ${anioNum}`}
        categories={categories}
        series={anyData ? series : []}
        height={height}
        chartStacked={false}
        gridShow
        dataLabelsEnabled={false}
        tooltipEnabled
        xaxisTickPlacement="between"
        colors={colors}
        noBoxShadow={noBoxShadow}
        disabled={!empresaId}
        loading={false}
        titleJustifyContent="center"
        alignTitleItems="center"
        //dualY
        legendBottom
        y1Min={0}
        y2Min={0}
        y2Max={y2Max}
      />

      {loading && (
        <div className={classes.spinnerOverlay} style={{ height: typeof height === 'number' ? height : 250 }}>
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default BarEquilibrio;
